import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Sparkles, Send, Loader2, ChevronDown, Network, Users } from "lucide-react";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";

const ease = [0.16, 1, 0.3, 1];

function SourceList({ sources, label }) {
  const [open, setOpen] = useState(false);
  if (!sources?.length) return null;
  return (
    <div className="mt-3 border-t border-navy/10 pt-2">
      <button onClick={() => setOpen((o) => !o)} data-testid="copilot-sources-toggle"
        className="inline-flex items-center gap-1.5 text-xs text-navy/50 hover:text-violet transition-colors">
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        {label} ({sources.length})
      </button>
      {open && (
        <ol className="mt-2 space-y-1.5">
          {sources.map((s) => (
            <li key={s.i} className="text-[11px] text-navy/60 flex gap-2" data-testid={`copilot-source-${s.i}`}>
              <span className="font-mono text-violet shrink-0">[{s.i}]</span>
              <span><span className="font-mono text-navy/40">{s.event_type}</span> · {s.summary} <span className="text-navy/30">· {(s.at || "").slice(0, 10)}</span></span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function KnowledgePanel({ t }) {
  const [k, setK] = useState(null);
  useEffect(() => { api.get("/ai/knowledge/summary").then((r) => setK(r.data)).catch(() => {}); }, []);
  if (!k) return null;
  return (
    <div className="card-surface p-5" data-testid="copilot-knowledge">
      <div className="flex items-center gap-2 mb-3">
        <Network className="w-4 h-4 text-violet" />
        <h3 className="font-medium">{t("os.copiloto.knowledge")}</h3>
        <span className="badge badge-violet ml-auto">{k.events_total} {t("os.copiloto.events")}</span>
      </div>
      <div className="text-xs text-navy/40 mb-1">{t("os.copiloto.byDomain")}</div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {k.by_domain.slice(0, 8).map((d) => (
          <span key={d.domain} className="badge badge-muted" data-testid={`kg-domain-${d.domain}`}>{d.domain} · {d.count}</span>
        ))}
      </div>
      {k.entities?.length > 0 && (
        <>
          <div className="text-xs text-navy/40 mb-1">{t("os.copiloto.entities")}</div>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {k.entities.map((e) => <span key={e.key} className="badge badge-emerald">{e.label} · {e.count}</span>)}
          </div>
        </>
      )}
      {k.top_actors?.length > 0 && (
        <>
          <div className="text-xs text-navy/40 mb-1 flex items-center gap-1"><Users className="w-3 h-3" /> {t("os.copiloto.topActors")}</div>
          <div className="space-y-1">
            {k.top_actors.map((a, i) => (
              <div key={i} className="flex justify-between text-sm"><span>{a.name}</span><span className="text-navy/40">{a.events}</span></div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function Copiloto() {
  const { t, lang } = useI18n();
  const es = lang === "es";
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const ask = async (q) => {
    const query = (q ?? question).trim();
    if (!query || loading) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/ai/copilot", { question: query });
      setResult(r.data);
    } catch (e) {
      setResult({ answer: e?.response?.data?.detail || "Error", sources: [] });
    } finally { setLoading(false); }
  };

  const suggestions = [t("os.copiloto.s1"), t("os.copiloto.s2"), t("os.copiloto.s3")];

  return (
    <OSLayout title={es ? "Copiloto" : "Copilot"}>
      <div className="space-y-8" data-testid="copiloto-page">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
          <div className="overline text-navy/40 mb-2 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> {t("os.copiloto.overline")}</div>
          <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">{t("os.copiloto.title")}</h2>
          <p className="mt-2 text-navy/50 max-w-2xl">{t("os.copiloto.subtitle")}</p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="card-surface p-4">
              <div className="flex gap-2">
                <input ref={inputRef} value={question} onChange={(e) => setQuestion(e.target.value)} maxLength={1000}
                  onKeyDown={(e) => e.key === "Enter" && ask()} className="field flex-1"
                  placeholder={t("os.copiloto.placeholder")} data-testid="copilot-input" />
                <button onClick={() => ask()} disabled={loading || !question.trim()} className="btn btn-primary" data-testid="copilot-ask">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className="text-xs text-navy/40 self-center">{t("os.copiloto.suggested")}:</span>
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => { setQuestion(s); ask(s); }} data-testid={`copilot-suggested-${i}`}
                    className="badge badge-muted hover:badge-violet transition-colors">{s}</button>
                ))}
              </div>
            </div>

            {loading && (
              <div className="card-surface p-6 flex items-center gap-3 text-navy/50" data-testid="copilot-loading">
                <Loader2 className="w-5 h-5 animate-spin text-violet" /> {t("os.copiloto.thinking")}
              </div>
            )}

            {result && !loading && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
                className="card-surface p-6" data-testid="copilot-answer">
                <div className="prose prose-sm max-w-none whitespace-pre-wrap text-navy/80">{result.answer}</div>
                <SourceList sources={result.sources} label={t("os.copiloto.sources")} />
                {result.model && (
                  <div className="mt-3 flex items-center gap-1.5 text-[11px] text-navy/30">
                    <Sparkles className="w-3 h-3" /> {t("os.copiloto.grounded")} · {result.model}
                  </div>
                )}
              </motion.div>
            )}

            {!result && !loading && (
              <div className="card-surface py-16 text-center text-navy/40 text-sm" data-testid="copilot-empty">{t("os.copiloto.empty")}</div>
            )}
          </div>

          <div className="lg:col-span-1">
            <KnowledgePanel t={t} />
          </div>
        </div>
      </div>
    </OSLayout>
  );
}
